import { useState } from 'react';
import { Button, FoldPanel } from '@src/components';
import { SEASONS } from '@src/theme/season';
import { GiftHelpPreview } from '../BoxPage/full/GiftHelpPreview';
import { DEFAULT_GIFT_PRESET, GIFT_PRESET_KEY, giftPresetId, readGiftPresets, type GiftPreviewPreset } from '../BoxPage/full/giftPreviewPresets';
import '../BoxPage/full/GifteeFramePreview.css';
import './GiftPreviewPresetsSection.css';

const controls: { key: keyof GiftPreviewPreset; label: string; min: number; max: number; step: number }[] = [
  { key: 'panX', label: 'Scene left / right', min: -400, max: 400, step: 1 },
  { key: 'panY', label: 'Scene up / down', min: -300, max: 300, step: 1 },
  { key: 'zoom', label: 'Scene zoom', min: .5, max: 2, step: .01 },
  { key: 'giftX', label: 'Gift left / right', min: -500, max: 500, step: 1 },
  { key: 'giftY', label: 'Gift up / down', min: -300, max: 300, step: 1 },
  { key: 'giftScale', label: 'Gift size', min: .4, max: 2, step: .01 },
];
export function GiftPreviewPresetsSection() {
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [mode, setMode] = useState<'light' | 'dark'>('dark');
  const [presets, setPresets] = useState(readGiftPresets);
  const [saved, setSaved] = useState(false);
  const update = (id: string, value: GiftPreviewPreset) => {
    setPresets(previous => ({ ...previous, [id]: value })); setSaved(false);
  };
  return <FoldPanel id="gift-preview-presets" title="Gift landscape presets" hint="Adjust all seven themes. Separate desktop/mobile and day/night settings. Mocked game data; live gift placement is not changed.">
    <div className="gift-preset-toolbar">
      <label>Viewport <select value={device} onChange={e => setDevice(e.target.value as typeof device)}><option value="desktop">Desktop · 1440 × 900</option><option value="mobile">Mobile · 390 × 844</option></select></label>
      <label>Appearance <select value={mode} onChange={e => setMode(e.target.value as typeof mode)}><option value="light">Day</option><option value="dark">Night</option></select></label>
      <Button onClick={() => { localStorage.setItem(GIFT_PRESET_KEY, JSON.stringify(presets)); setSaved(true); }}>Save locally</Button>
      <Button variant="soft" onClick={() => {
        const all = Object.fromEntries(SEASONS.flatMap(season => ['light','dark'].flatMap(color => [false,true].map(mobile => {
          const id = giftPresetId(season,mobile,color); return [id,presets[id] ?? DEFAULT_GIFT_PRESET];
        }))));
        const url = URL.createObjectURL(new Blob([JSON.stringify(all,null,2)], { type:'application/json' }));
        const link = document.createElement('a'); link.href=url; link.download='gift-preview-presets.json'; link.click(); setTimeout(() => URL.revokeObjectURL(url),1000);
      }}>Export all presets</Button>
      <p role="status">{saved ? 'Saved. Reopen the gift explanation to see your changes.' : 'Adjust the sliders, then save. Settings are stored in this browser; export them to apply permanently.'}</p>
    </div>
    <div className="gift-preset-grid">
      {SEASONS.map(season => {
        const id = giftPresetId(season,device === 'mobile',mode);
        const preset = presets[id] ?? DEFAULT_GIFT_PRESET;
        return <section key={id} className="gift-preset-card" data-preset-theme={season}>
          <h3>{season}</h3>
          <GiftHelpPreview lazy season={season} mode={mode} device={device} preset={preset} />
          {controls.map(control => <label key={control.key} className="gift-preset-control">
            <span>{control.label}</span><output>{preset[control.key]}</output>
            <input type="range" aria-label={`${season}: ${control.label}`} min={control.min} max={control.max} step={control.step} value={preset[control.key]} onChange={e => update(id,{...preset,[control.key]:Number(e.target.value)})} />
          </label>)}
          <Button variant="soft" size="small" onClick={() => update(id,{...DEFAULT_GIFT_PRESET})}>Reset this preset</Button>
        </section>;
      })}
    </div>
  </FoldPanel>;
}
